import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useProAuth } from '@/lib/ProAuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, MessageSquare, Home, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Wallet Success Page
 * Shows after successful SMS credit purchase
 */
export const WalletSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useProAuth();
  const [status, setStatus] = useState('loading');
  const [purchaseData, setPurchaseData] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');
  const packageId = searchParams.get('package');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('Missing session ID');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/sms-wallet/purchase/status/${sessionId}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error('Failed to verify purchase');
        }

        const data = await response.json();
        
        if (data.status === 'completed') {
          setStatus('success');
          setPurchaseData(data);
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          setStatus('pending');
        }
      } catch (err) {
        setStatus('error');
        setError(err.message);
      }
    };

    checkStatus();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold">Verifying purchase...</h2>
            <p className="text-gray-500 mt-2">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700">
              {status === 'expired' ? 'Purchase Expired' : 'Something went wrong'}
            </h2>
            <p className="text-gray-600 mt-2">
              {error || 'The checkout session has expired. Please try again.'}
            </p>
            <div className="flex gap-3 mt-6 justify-center">
              <Button variant="outline" asChild>
                <Link to="/">
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Link>
              </Button>
              <Button asChild>
                <Link to="/pro/dashboard">
                  Try Again
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Credits Added!
          </h2>
          
          <p className="text-gray-600 mb-6">
            Your SMS credits have been successfully added to your wallet.
          </p>

          {purchaseData && (
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <MessageSquare className="h-5 w-5" />
                <span className="text-2xl font-bold">+{purchaseData.credits_added}</span>
                <span className="text-sm">SMS credits</span>
              </div>
              <p className="text-sm text-green-600 mt-2">
                New balance: {purchaseData.wallet_balance} credits available
              </p>
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <Button variant="outline" asChild>
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Link>
            </Button>
            <Button asChild>
              <Link to="/pro/dashboard">
                <MessageSquare className="h-4 w-4 mr-2" />
                Dashboard
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Wallet Cancel Page
 * Shows when user cancels SMS credit purchase
 */
export const WalletCancelPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-10 w-10 text-gray-400" />
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Purchase Cancelled
          </h2>
          
          <p className="text-gray-600 mb-6">
            Your purchase was cancelled. No charges were made.
          </p>

          <div className="flex gap-3 justify-center">
            <Button variant="outline" asChild>
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Link>
            </Button>
            <Button asChild>
              <Link to="/pro/dashboard">
                Try Again
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WalletSuccessPage;
